import type { Logger } from "@expert/logging";
import type { GaiaPayloadConfigs, OrchestratorInvokeMessagePayload } from "@expert/shared-types";

import { fallbackGaiaPayloadProps, getFallbackPartner, useGaiaStore, useGaiaWebsocket } from "@expert/gaia";
import { registerDeviceProbe } from "@expert/solve-tools";
import { useCallback, useMemo } from "react";

import { useTimelineStore } from "../state";

/**
 * Scoped to the activation intent rather than "what device was mentioned" — customers commonly refer to
 * their old handset. Names no device: GAIA resolves it from the transcript, so one here would bias it.
 */
const DEVICE_PROBE_PROMPT = "What device is the customer trying to activate?";

/**
 * Sends a reactive invoke purely to harvest the device GAIA attaches to reactive responses. Adds no
 * timeline message and dispatches no analytics; `useGaiaMessageEvents` matches the `correlationId` and
 * drops the answer text.
 *
 * Needed because the proactive payload carries no device fields, so the device cannot arrive unprompted.
 */
export function useRequestDeviceInfo(loggerProp: Logger, gaiaPayloadProps: GaiaPayloadConfigs) {
    const websocketObj = useGaiaWebsocket();
    const subscriptionStatus = useGaiaStore((state) => state.subscriptionStatus);
    const expertId = useTimelineStore((state) => state.expertId);
    const currentSessionId = useTimelineStore((state) => state.currentSessionId);
    const getTimelineState = useTimelineStore((state) => state.getTimelineState);

    const logger = useMemo(() => loggerProp.child({ module: "useRequestDeviceInfo" }), [loggerProp]);

    /**
     * @param sessionIdOverride required from per-session callers — on chat the selected session may not be
     * the one being probed.
     * @returns whether it was sent. Gate any "already probed" marking on this, or a bail-out here retires
     * the probe for a session it never ran on.
     */
    const requestDeviceInfo = useCallback(
        (sessionIdOverride?: string): boolean => {
            const sessionId = sessionIdOverride ?? currentSessionId;
            if (!sessionId) {
                return false;
            }

            if (!websocketObj) {
                logger.debug({ sessionId }, "Device probe skipped — no GAIA websocket");
                return false;
            }

            // No subscribe-then-retry as a real message does — forcing one could race session setup.
            if (subscriptionStatus[sessionId] !== "subscribed") {
                logger.debug({ sessionId }, "Device probe skipped — session not subscribed to GAIA");
                return false;
            }

            const { callSid, chatId, partner } = getTimelineState(sessionId);
            const { appName } = gaiaPayloadProps ?? fallbackGaiaPayloadProps(chatId ? "chat" : "voice");
            const correlationId = crypto.randomUUID();

            const payload: OrchestratorInvokeMessagePayload = {
                action: "orchestrator-invoke",
                appName,
                correlationId,
                message: DEVICE_PROBE_PROMPT,
                messageId: crypto.randomUUID(),
                // Must stay "Search": only the reactive solve workflow runs device detection.
                messageType: "Search",
                partner: getFallbackPartner({ partner }),
                sessionId: chatId ?? callSid ?? sessionId,
                sessionGroupId: sessionId,
                userId: expertId,
                userType: "expert",
            };

            registerDeviceProbe(correlationId);

            websocketObj.sendJsonMessage(payload);
            logger.info({ correlationId, sessionId }, "Device probe sent to GAIA");
            return true;
        },
        [currentSessionId, expertId, gaiaPayloadProps, getTimelineState, logger, subscriptionStatus, websocketObj],
    );

    return useMemo(() => ({ requestDeviceInfo }), [requestDeviceInfo]);
}
